import { useEffect } from 'react';
import { hasOpenSurface } from './useDismiss';

/**
 * The house typing guard.
 *
 * A shortcut must never fire while somebody is filling in a field — `x` in a task title has to
 * stay an `x`. Five screens bind keys on `document` now — see the scopes in `keys.ts` — and a
 * copy of this test per screen is a chance per screen for one of them to drift and start eating
 * keystrokes out of an input. Today had exactly that: its own inline copy, written before this
 * file existed.
 */
export function isTypingTarget(): boolean {
  const el = document.activeElement;
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  );
}

/**
 * A focused control on a surface the user has entered owns Enter and Space.
 *
 * The list and the board bind Enter (open the cursor task) and Space (select the cursor row) on
 * `document`, and both call `preventDefault` — which in Chrome is also what cancels a focused
 * button's own activation. So a keyboard user who selected two rows, Tabbed to the selection
 * bar's *Edit…* and pressed Enter got the cursor task's panel instead of the popover, and at 375
 * the full task view, with the selection thrown away. Space toggled the cursor row instead of
 * pressing the button. The verbs on the bar were unreachable without a mouse.
 *
 * The rule is *what the focus is in*, opted into by surface: the selection bar, the task panel,
 * a dialog, a menu. Only Enter and Space, the two keys a button answers to — `j`, `x` and the
 * digits stay the list's wherever focus is.
 *
 * Why not every control outside the rows, which was the first version and is narrower than "any
 * button": a mouse click on the *Tasks* nav tab or the *Board* toggle leaves DOM focus on that
 * button too (measured: the keydown after the click lands on `button.tab "Tasks"`), so "click
 * Board, `j`, Space" pressed the toggle and selected nothing — six specs reproduce that gesture and
 * all six went red. Clicking the header is navigation *into* the list, after which the list owns
 * the keyboard; the rows are the same case, and the row's `onMouseDown` exists so the next key acts
 * on the row you touched. Nor by input modality: `:focus-visible` cannot tell a clicked button from
 * a Tabbed one at keydown, because Chrome turns it on for the keypress itself, and the panel and
 * dialog are about to receive focus programmatically, which is neither. A header control reached
 * by Tab still loses Enter to the list; that half is `01a17671-02b0`.
 */
export function focusOwnsKey(e: KeyboardEvent): boolean {
  if (e.key !== 'Enter' && e.key !== ' ') return false;
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  if (!el.matches('button, a[href], [role=button], [role=checkbox], [role=menuitem], summary')) {
    return false;
  }
  return el.closest('.command-bar, .detail, [role=dialog], [role=menu]') !== null;
}

/**
 * Escape hands the keyboard back.
 *
 * The other half of the guard above, and it did not exist. Every global key — `c`, `/`, `?` —
 * refuses to fire while a field has focus, which is right; so there has to be one gesture that
 * ends "filling in a field", and `Anywhere` has always said which one it is: *"Esc — Close
 * whatever is open"*. On the Tasks screen it was not true. `c` focused the inline capture box,
 * Escape did nothing to it, and every key after it went in as text — `c Esc ? x` left the literal
 * `?x` in the box, no Keys panel, nothing completed, and the keyboard dead until the mouse clicked
 * elsewhere. Today and Goals only looked right: there `c` opens the header's disclosure, and
 * Escape closes it, so the input is *unmounted* from under the focus rather than released.
 *
 * Installed once for the app, not per screen, and deliberately not in either of the two places
 * that surfaced it:
 *
 * - Not in `TaskList`'s Escape branch, where the report found it. That is one screen's handler,
 *   and the field this releases is in the *header* on all the others — a screen-shaped fix for a
 *   keyboard-shaped rule.
 * - Not in `useKeys`, tempting as the new registry is. `useKeys` runs on the five screens that
 *   bind a scope; Habits, Projects, Labels, Reflection, Templates and Settings bind none and do
 *   not call it at all, and each of them carries the same header capture box. The property
 *   belongs to the one keyboard, which is what this file is about, so it sits beside the guard
 *   whose counterpart it is.
 *
 * **Ordering, because Escape already has jobs.** Four handlers layer explicitly and stand down on
 * `defaultPrevented` — the list's and the board's selection-clear, `TaskView`'s back, and
 * `TaskDetail`'s close — so this listens in the **capture** phase and calls `preventDefault`,
 * which is what makes them stand down: in bubble order the list's listener runs first (its effect
 * is a child's, registered before this one) and one press would both release the field and wipe a
 * batch that took twelve clicks to assemble, which is the harm `hasOpenSurface` exists to prevent.
 * It does **not** `stopPropagation`: everything else listening for Escape means "close my thing"
 * — the capture disclosure, a menu, the search panel, Today's abandon-the-drawn-block — and
 * closing what is open is exactly what the promise says happens.
 *
 * And it stands down entirely for anything open over the page. A picker's filter field is a typing
 * target too, and there Escape belongs to the picker; blurring it would leave the popup up with
 * the focus nowhere. `Select` and `MultiSelect` answer on the element and `stopPropagation`, but
 * they are React handlers at the root container and a capture listener on `document` runs before
 * them, so asking `hasOpenSurface` first is what actually yields.
 *
 * Release only. Clearing the draft was the other half of the suggestion and is a second decision:
 * "give me the keyboard back" and "throw away what I typed" are different requests, and this repo
 * has already paid once for an Escape that discarded work nobody asked it to discard.
 */
export function useEscapeReleasesFields(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (hasOpenSurface() || !isTypingTarget()) return;
      const field = document.activeElement;
      if (!(field instanceof HTMLElement)) return;
      event.preventDefault();
      /*
        Into the surface the field sits in, when that surface takes focus — the task panel and the
        full view do (`useFocusOnOpen`). A bare `blur()` there put focus on `<body>`, so the next
        Escape still closed the panel but the Tab after a release started at the top nav, which is
        the very drift the panel's focus handling exists to stop. Outside such a surface the field
        is simply blurred, as before.
      */
      const home = field.parentElement?.closest<HTMLElement>('[tabindex="-1"]');
      if (home) home.focus({ preventScroll: true });
      else field.blur();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);
}
